import {gql, useQuery} from "@apollo/client";
import {Challenge, ChallengeMode, Scalars, Scenario, CustomParameter} from "../../../schema";
import {Action, ActionInput} from "../../../types";
import AvatarActionEditForm from "./AvatarActionEditForm";
import SystemActionEditForm from "./SystemActionEditForm";
import UserInputActionEditForm from "./UserInputActionEditForm";
import ChoiceUserInputActionEditForm from "./ChoiceUserInputActionEditForm";
import {competenciesQuery, competenciesQueryData} from "./ActionEditForm.graphql";

type opts = {
  action: Action;
  testId?: string;
  branchId?: Scalars["ID"];
  scenarios?: Scenario[];
  noAvatar?: boolean;
  inputPhraseLimit?: Scalars["Int"];
  avatarPhraseLimit?: Scalars["Int"];
  systemPhraseLimit?: Scalars["Int"];
  submitDisabled?: boolean;
  onSubmit?: (data: ActionInput) => void;
  customScores?: boolean;
  customParametersSupport?: boolean;
  customParameters?: CustomParameter[],
  challenge?: Challenge;
};

export default function ActionEditForm({
  action,
  testId,
  branchId,
  scenarios,
  noAvatar,
  customScores,
  customParametersSupport,
  customParameters,
  challenge,
  inputPhraseLimit,
  avatarPhraseLimit,
  systemPhraseLimit,
  submitDisabled,
  onSubmit,
}: opts) {
  const {competencies} = useQuery<competenciesQueryData, competenciesQueryData>(competenciesQuery).data ?? {};

  const submit = submitDisabled ? undefined : onSubmit;
  switch (action.__typename) {
    case "AvatarAction":
      return (
        <AvatarActionEditForm
          testId={testId}
          action={action}
          phraseLimit={avatarPhraseLimit}
          noAvatar={noAvatar}
          onSubmit={submit}
          challenge={challenge}
        />
      );

    case "UserInputAction":
      return (
        <UserInputActionEditForm
          testId={testId}
          action={action}
          competencies={competencies}
          phraseLimit={inputPhraseLimit}
          onSubmit={submit}
          challenge={challenge}
        />
      );

    case "SystemAction":
      return (
        <SystemActionEditForm
          testId={testId}
          action={action}
          phraseLimit={systemPhraseLimit}
          onSubmit={submit}
          challenge={challenge}
        />
      );

    case "ChoiceUserInputAction":
      return (
        <ChoiceUserInputActionEditForm
          testId={testId}
          action={action}
          scenarios={scenarios}
          branchId={branchId!}
          customScoreSupport={customScores}
          customParametersSupport={customParametersSupport}
          customParameters={customParameters}
          phraseLimit={inputPhraseLimit}
          onSubmit={submit}
          allowScenarios={challenge ? challenge.mode !== ChallengeMode.COMPETENCE_TEST : true}
        />
      );

    default:
      return null;
  }
}

ActionEditForm.fragments = {
  root: gql`
    fragment ActionEditForm on Action {
      id

      ... on AvatarAction {
        ...AvatarActionEditForm
      }

      ... on UserInputAction {
        ...UserInputActionEditForm
      }

      ... on SystemAction {
        ...SystemActionEditForm
      }

      ... on ChoiceUserInputAction {
        ...ChoiceUserInputActionEditForm
      }
    }

    ${AvatarActionEditForm.fragments.root}
    ${UserInputActionEditForm.fragments.root}
    ${SystemActionEditForm.fragments.root}
    ${ChoiceUserInputActionEditForm.fragments.root}
  `,
};

// For Avatar and System text or anything else using TTS
export function limitSpeechTextOnChange(value: string, maxLength: number) {
  const regexp = /(.*?)(?:\+|$)(\w|[а-яА-Я]|\n|$)/gm;
  let m;
  let length = 0;
  let text = "";

  while ((m = regexp.exec(value ?? "")) !== null && length < maxLength) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (m.index === regexp.lastIndex) {
      regexp.lastIndex++;
    }
    if (length + m[1].length + m[2].length > maxLength) {
      text += m[0].slice(0, maxLength - length);
      length = maxLength;
    } else {
      length += m[1].length + m[2].length;
      text += m[0];
    }
  }

  return {value: text, length};
}
